<script lang="ts" setup>
import BaseCard from "@/components/BaseCard.vue";
import useCharacter from "@/composables/character";
import { Character } from "@/types/character";
import _ from "lodash";
import { computed, toRef } from "vue";
import { route } from "ziggy-js";
import BaseContent from "./BaseContent.vue";

interface Props {
  character: Character;
  eza?: boolean;
  compact?: boolean;
}

const props = defineProps<Props>();

const { leaderSkill } = useCharacter(props.character, toRef(props, "eza"));
const effectDescriptions = computed(() => {
  return leaderSkill.value.description.split(/; |、/).map((e) => {
    return e.split(/,[\n\r|\s]+plus /).map((se) => {
      return _.upperFirst(se.replace(/[\n\r]/g, ""));
    });
  });
});

const categoriesPrimary = computed(() => {
  return _.flatten(
    props.character.leader_skill.effects
      .filter(
        (effect) =>
          [82, 104].includes(effect.type) && effect.categories.length === 1,
      )
      .map((effect) => effect.categories.map((category) => category.id)),
  );
});

const categoriesAdditional = computed(() => {
  return _.uniq(
    _.flatten(
      props.character.leader_skill.effects
        .filter(
          (effect) =>
            [82, 104].includes(effect.type) && effect.categories.length === 2,
        )
        .map((effect) => effect.categories.map((category) => category.id)),
    ),
  ).filter((effect) => !categoriesPrimary.value.includes(effect));
});
</script>

<template>
  <BaseCard
    v-if="!compact"
    :footer="{
      href: route('characters.index', {
        categories: categoriesPrimary,
        categories_additional: categoriesAdditional,
      }),
    }"
  >
    <template #heading>Leader Skill</template>

    <template v-if="categoriesPrimary.length > 0" #footer
      >Show Teammates
    </template>

    <BaseContent class="flex flex-col gap-4">
      <template
        v-if="
          effectDescriptions.length === 1 && effectDescriptions[0].length === 1
        "
      >
        {{ effectDescriptions[0][0] }}
      </template>
      <ul v-else class="ml-4 flex list-disc flex-col gap-1">
        <li v-for="effectDescription in effectDescriptions">
          {{ effectDescription[0] }}
          <ul class="ml-2.5 flex list-['+'] flex-col gap-1">
            <li v-for="subEffect in effectDescription.slice(1)" class="pl-2">
              {{ subEffect }}
            </li>
          </ul>
        </li>
      </ul>
    </BaseContent>
  </BaseCard>
  <div v-else class="flex flex-col gap-4">
    <BaseContent>
      <template #header>Leader Skill</template>
      {{ leaderSkill.description }}
    </BaseContent>
  </div>
</template>
